import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { useUser } from '/imports/hooks';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { PageFooter } from '/imports/ui/PageFooter';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { toastOnError } from '/imports/ui/utils';
import {
  createSolarEdgeApiKey, getSolarEdgeApiKeysForOrg, SolarEdgeApiKeyListing, pullSolarEdgeSites
} from '/imports/api/solarEdgeMeters';

const SolarEdgeMeterPageStyled = styled(Page)`
  .result {
    margin: 2rem 0;
    .buttons {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }
  }
  table {
    td {
      padding: 1rem;
      &:first-child {
        padding-left: 0;
      }
    }
  }
  textarea {
    height: 14rem;
  }
  #import-file-input {
    display: none;
  }
`;

/** Super-user page for managing SolarEdge API keys and pulling sites. */
export const SolarEdgeMeterPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;
  const orgId = user.orgId;
  const [apiKey, setApiKey] = useState<string>();
  const [apiKeys, setSolarEdgeApiKeys] = useState<SolarEdgeApiKeyListing[]>([]);
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setSolarEdgeApiKeys(await getSolarEdgeApiKeysForOrg.call(orgId));
    })();
  }, [orgId]);
  /**
   * Pulls SolarEdge sites for the given API key and toasts the result counts.
   * @param tokenId - id of the stored SolarEdge API key
   */
  const pullSites = toastOnError(async (tokenId: string) => {
    const { numSystems, numCreated } = await pullSolarEdgeSites.call(tokenId);
    toast.success(`${numSystems} ${strings.SystemsFound()} | ${numCreated} ${strings.Created()}`);
  });
  /** Persists the entered API key and refreshes the key list. */
  const save = toastOnError(async () => {
    if (!apiKey || !orgId) return;
    await createSolarEdgeApiKey.call({ apiKey });
    setSolarEdgeApiKeys(await getSolarEdgeApiKeysForOrg.call(orgId));
  });
  return (
    <SolarEdgeMeterPageStyled className="page">
      <PageHeading
        left={<h1>SolarEdge</h1>}
      />
      <PageBody>
        <Form>
          <div className="field-group">
            <FormField field="apiKey">
              <label>{strings.ApiKey()}</label>
              <Input value={apiKey}
                onChange={(apiKey) => setApiKey(apiKey)} />
            </FormField>
          </div>
          <div><Button onClick={() => save()}>{strings.Save()}</Button></div>
        </Form>
        {
          apiKeys.length ?
            <div>
              <h2>{strings.Accounts()}</h2>
              <table>
                <tbody>
                  {
                    apiKeys.map((t) => (
                      <tr key={t._id}>
                        <td>{t.apiKeyFirst}..........................{t.apiKeyLast}</td>
                        <td><Button onClick={() => pullSites(t._id)}>{strings.PullSystems()}</Button></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            :
            null
        }
      </PageBody>
      <PageFooter
      />
    </SolarEdgeMeterPageStyled>
  );
};
